'use client';
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  getBuilding,
  getBuildingAddresses,
  getBuildingRatings,
  getBuildingResponses,
  getBuildingReviews,
  trackBuildingView,
  updateBuildingOpenData,
  type Building,
  type BuildingAggResponse,
  type BuildingAddress,
  type ExplorerData,
  type ReviewApi,
} from '@repo/api';
import { runWithDedup } from '@repo/store';
import { useStore } from '@/providers/StoreProvider';
import { useStoreContext } from '@repo/store/web';
import { useSearchParams } from 'next/navigation';
export type BuildingTab = 'overview' | 'report';

export interface BuildingReportData {
  violations: {
    classC: number;
    classB: number;
    classA: number;
    classI: number;
  };
  totalViolations: number;
  units: Array<{
    unit: string;
    floor: number;
    violationCount: number;
  }>;
  totalUnits: number;
  bedbugs: {
    hasBedbugs: boolean;
    lastFilingDate?: string;
  };
  rodents: {
    passedInspection: boolean;
    lastInspectionDate?: string;
  };
  construction: Array<{
    type: 'major' | 'minor';
    count: number;
    description: string;
  }>;
}

export interface BuildingContextValue {
  building: Building;
  buildingReviews: { data: ReviewApi[]; count: number };
  reviewsHasMore: boolean;
  reviewsLoadingMore: boolean;
  fetchMoreReviews: () => Promise<void>;
  buildingRatings: Record<string, number>;
  aggResponse: BuildingAggResponse | null;
  buildingAddresses: BuildingAddress[] | null;
  isMasked: boolean;
  loading: boolean;
  error: string | null;
  activeTab: BuildingTab;
  setActiveTab: (tab: BuildingTab) => void;
  reportData: BuildingReportData | null;
  reportLoading: boolean;
  reportError: string | null;
}
export interface BuildingProviderProps {
  children: React.ReactNode;
  building: Building;
  explorerData: ExplorerData | null;
  /**
   * Verified alternate addresses, fetched during the server render so the
   * aliases are in the initial HTML. `null` means the server call failed (or
   * a caller hasn't been updated), in which case the effect below still
   * fetches them client-side — the popup keeps working either way.
   */
  initialBuildingAddresses?: BuildingAddress[] | null;
}

// Create the context
export const BuildingContext = React.createContext<BuildingContextValue | null>(null);

export function BuildingProvider({
  children,
  building,
  explorerData: initialExplorerData,
  initialBuildingAddresses = null,
}: BuildingProviderProps) {
  const isLoggedIn = useStore(state => state.webAuth.isLoggedIn);
  const sessionHydrated = useStore(state => state.webAuth.sessionHydrated);
  const storedExplorerData = useStore(state => state.explorer.data);
  const reviewerData = useStore(state => state.reviewer.data);
  const isMasked = useStore(state => state.profile.isBlurRequired);
  // Important: don't trust `initialExplorerData` after logout during client-side back/forward.
  // Only use it when we currently consider the user logged in AND auth is hydrated.
  const explorerData =
    sessionHydrated && isLoggedIn ? storedExplorerData || initialExplorerData : null;
  const setExplorerData = useStore(state => state.explorer.setExplorerData);
  const store = useStoreContext();
  const searchParams = useSearchParams();

  // Set initial explorer data from server in the store if it exists and store doesn't have it
  useEffect(() => {
    if (initialExplorerData !== null && initialExplorerData !== undefined) {
      // Check current store state directly
      const currentExplorerData = store.getState().explorer.data;
      if (!currentExplorerData) {
        // Transform API ExplorerData to store ExplorerData (handle null values)
        const storeExplorerData = {
          ...initialExplorerData,
          accessType: initialExplorerData.accessType || '',
          accessSubscriptionId: initialExplorerData.accessSubscriptionId || '',
          accessStart: initialExplorerData.accessStart || '',
        };
        setExplorerData(storeExplorerData);
      }
    }
  }, [initialExplorerData, setExplorerData, store]);

  // Initialize activeTab from URL query parameter
  const initialTab = searchParams.get('buildingReport') === 'true' ? 'report' : 'overview';
  const buildingSlug = building.slug;
  const lastFetchedReviewsKeyRef = useRef<string | null>(null);
  // Auth-only: record a building view once per building (so the request carries
  // the session id + auth). Mirrors the unit track_view behavior.
  const trackedBuildingSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (!buildingSlug || !isLoggedIn || !sessionHydrated) return;
    if (trackedBuildingSlugRef.current === buildingSlug) return;
    trackedBuildingSlugRef.current = buildingSlug;
    void trackBuildingView(buildingSlug);
  }, [buildingSlug, isLoggedIn, sessionHydrated]);
  // Hard lock to prevent concurrent pagination fetches (IntersectionObserver can fire rapidly).
  const reviewsFetchLockRef = useRef(false);
  const REVIEWS_PAGE_SIZE = 5;

  const [buildingReviews, setBuildingReviews] = useState<{ data: ReviewApi[]; count: number }>({
    data: (building.seoReviews as ReviewApi[]) || [],
    count: building.reviewCount || 0,
  });
  const [reviewsPage, setReviewsPage] = useState(1);
  const [reviewsHasMore, setReviewsHasMore] = useState(false);
  const [reviewsLoadingMore, setReviewsLoadingMore] = useState(false);
  const [aggResponse, setAggResponse] = useState<BuildingAggResponse | null>(null);
  const [buildingRatings, setBuildingRatings] = useState<any | null>(null);
  // Seeded from the server render. An empty array is a real answer (this
  // building has no aliases) and is truthy, so the effect below won't refetch;
  // only a `null` — server call failed — falls through to the client fetch.
  const [buildingAddresses, setBuildingAddresses] = useState<BuildingAddress[] | null>(
    initialBuildingAddresses,
  );
  const [currentBuilding, setCurrentBuilding] = useState<Building>(building);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<BuildingTab>(initialTab);
  const [reportData, setReportData] = useState<BuildingReportData | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  // Avoid repeating the open data update check for the same building.
  const openDataCheckedBuildingRef = useRef<string | null>(null);

  const isUserReviewer = useMemo(() => {
    // Check reviewerData first (fallback)
    const hasReviewerAccess = reviewerData?.hasReviewerAccess || false;

    // If no explorerData, only check reviewerData
    if (!explorerData) {
      return hasReviewerAccess;
    }

    // Check explorerData access
    const hasExplorerAccess =
      Boolean(explorerData.accessType) ||
      Boolean(
        explorerData.freeAccessBuildings?.some(
          (building: { slug?: string }) => building?.slug === buildingSlug,
        ),
      );

    return hasExplorerAccess || hasReviewerAccess;
  }, [explorerData, reviewerData, buildingSlug]);

  // Sync activeTab with URL query parameter changes
  useEffect(() => {
    const reportParam = searchParams.get('buildingReport');
    if (reportParam === 'true' && activeTab !== 'report') {
      setActiveTab('report');
    } else if (reportParam !== 'true' && activeTab !== 'overview') {
      setActiveTab('overview');
    }
  }, [searchParams, activeTab]);

  /**
   * After building is fetched, trigger an OpenData refresh once.
   * If backend says data was updated, refetch building and replace state.
   */
  const refreshBuildingFromOpenDataIfNeeded = useCallback(async (buildingData: Building) => {
    const cityId = (buildingData as any)?.primaryAddress?.cityId;
    const buildingSlug = buildingData?.slug;

    if (!buildingSlug) return;
    if (cityId !== 'nyc') return;
    if (openDataCheckedBuildingRef.current === buildingSlug) return;

    openDataCheckedBuildingRef.current = buildingSlug;

    try {
      const resp = await updateBuildingOpenData(buildingSlug);
      if (resp?.updated) {
        // Get buildingId and addressSlug from the building data
        const buildingId = buildingData.id;
        const addressSlug = (buildingData as any)?.primaryAddress?.slug;
        if (buildingId && addressSlug) {
          const freshBuilding = await getBuilding(buildingSlug, addressSlug);
          setCurrentBuilding(freshBuilding);
        }
      }
    } catch {
      // Fail open (endpoint might not be available in some envs)
    }
  }, []);

  const fetchBuildingAddresses = useCallback(async () => {
    const addresses = await runWithDedup(`building.addresses:${buildingSlug}`, () =>
      getBuildingAddresses(buildingSlug),
    );
    setBuildingAddresses(addresses.results);
  }, [buildingSlug]);

  // Fallback only. With the server-rendered array present this is a no-op;
  // it still covers the case where that fetch failed.
  useEffect(() => {
    if (!buildingAddresses) {
      void fetchBuildingAddresses();
    }
  }, [buildingAddresses, fetchBuildingAddresses]);

  // Sync building prop with state when it changes
  useEffect(() => {
    setCurrentBuilding(building);
  }, [building]);

  // Refresh building from open data after initial load
  useEffect(() => {
    if (currentBuilding) {
      void refreshBuildingFromOpenDataIfNeeded(currentBuilding);
    }
  }, [currentBuilding, refreshBuildingFromOpenDataIfNeeded]);

  const fetchBuildingRelatedData = useCallback(async () => {
    // Only fetch building other data if user has reviewer access
    if (!isUserReviewer) {
      setLoading(false);
      return;
    }

    const key = `${buildingSlug}:${explorerData?.accessType ?? 'none'}`;
    // Prevent double-fetch in React Strict Mode dev mounts / re-renders.
    if (lastFetchedReviewsKeyRef.current === key) return;
    lastFetchedReviewsKeyRef.current = key;
    try {
      setLoading(true);
      setError(null);

      const { aggResponseData, reviewsData, ratingsData } = await runWithDedup(
        `building.related:${key}`,
        async () => {
          // Only fetch building other data if user has reviewer access
          const promises: [Promise<BuildingAggResponse | null>, Promise<any>, Promise<any>] = [
            getBuildingResponses(buildingSlug).catch(() => null),
            getBuildingReviews(buildingSlug, undefined, 1, REVIEWS_PAGE_SIZE).catch(() => ({
              results: [] as ReviewApi[],
              count: 0,
              next: null as string | null,
            })),
            getBuildingRatings(buildingSlug).catch(() => null),
          ];

          const [aggResponseData, reviewsData, ratingsData] = await Promise.all(promises);
          return { aggResponseData, reviewsData, ratingsData };
        },
      );

      setBuildingReviews({
        data: reviewsData.results || [],
        count: reviewsData.count || 0,
      });
      setReviewsPage(1);
      setReviewsHasMore(!!reviewsData.next);
      setAggResponse(aggResponseData);
      setBuildingRatings(ratingsData);
    } catch (err) {
      console.error('Error fetching building details:', err);
      setError('Failed to load building details');
      setAggResponse(null);
      setBuildingRatings(null);
      setReviewsHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [buildingSlug, explorerData?.accessType, isUserReviewer]);

  const fetchMoreReviews = useCallback(async () => {
    if (!isUserReviewer) return;
    if (!reviewsHasMore) return;
    // Prevent concurrent calls even before React state updates land.
    if (reviewsFetchLockRef.current) return;
    reviewsFetchLockRef.current = true;

    const key = `${buildingSlug}:${explorerData?.accessType ?? 'none'}`;
    const nextPage = reviewsPage + 1;

    try {
      setReviewsLoadingMore(true);
      const reviewsData = await runWithDedup(`building.reviews:${key}:page:${nextPage}`, () =>
        getBuildingReviews(buildingSlug, undefined, nextPage, REVIEWS_PAGE_SIZE),
      );

      setBuildingReviews(prev => {
        const seen = new Set(prev.data.map(r => r.id));
        const nextResults = (reviewsData.results || []).filter(r => !seen.has(r.id));
        return {
          data: [...prev.data, ...nextResults],
          count: typeof reviewsData.count === 'number' ? reviewsData.count : prev.count,
        };
      });
      setReviewsPage(nextPage);
      setReviewsHasMore(!!reviewsData.next);
    } catch (err) {
      console.error('Error fetching more building reviews:', err);
    } finally {
      setReviewsLoadingMore(false);
      reviewsFetchLockRef.current = false;
    }
  }, [buildingSlug, explorerData?.accessType, isUserReviewer, reviewsHasMore, reviewsPage]);

  useEffect(() => {
    if (isUserReviewer) {
      void fetchBuildingRelatedData();
    } else {
      // If user doesn't have reviewer access, set loading to false
      setLoading(false);
    }
  }, [isUserReviewer, fetchBuildingRelatedData]);

  useEffect(() => {
    // Only fetch report data if user has access (isMasked is false)
    if (!isMasked && activeTab === 'report' && !reportData && !reportLoading) {
      // TODO: Replace with actual API call when available
      // For now, we'll use placeholder data in ReportDetailContent
      setReportLoading(false);
    }
  }, [isMasked, activeTab, reportData, reportLoading]);

  useEffect(() => {
    if (!isLoggedIn || !sessionHydrated) {
      // setIsMasked(true);
      // allow a fresh fetch if user logs in again
      lastFetchedReviewsKeyRef.current = null;
      setReviewsPage(1);
      setReviewsHasMore(false);
      reviewsFetchLockRef.current = false;
    }
  }, [isLoggedIn, sessionHydrated]);

  /*
   * De-duplicated once, here, so every consumer agrees.
   *
   * building-svc returns the primary address both as its own record and as an
   * alias, so e.g. "343 Gold Street" arrives twice. That has always shown as a
   * repeated row in the popup; now that the aliases also render into the page
   * HTML it would be duplicated there too.
   *
   * Deliberately NOT truncated — the brief asks for the full verified array.
   *
   * Keyed on fullAddress (falling back to streetAddress), first occurrence
   * wins, so the primary address stays at the top of the list. Entries with
   * neither value are dropped; they would render as an empty row.
   */
  const dedupedBuildingAddresses = useMemo(() => {
    if (!buildingAddresses) return null;
    const seen = new Set<string>();
    return buildingAddresses.filter(address => {
      const key = (address.fullAddress || address.streetAddress || '').trim().toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [buildingAddresses]);

  const contextValue: BuildingContextValue = {
    building: currentBuilding,
    buildingReviews,
    reviewsHasMore,
    reviewsLoadingMore,
    fetchMoreReviews,
    buildingRatings,
    aggResponse,
    buildingAddresses: dedupedBuildingAddresses,
    isMasked,
    loading,
    error,
    activeTab,
    setActiveTab,
    reportData,
    reportLoading,
    reportError,
  };

  return <BuildingContext.Provider value={contextValue}>{children}</BuildingContext.Provider>;
}

// Custom hook for accessing building data (use this in client components)
export function useBuilding(): BuildingContextValue {
  const context = React.useContext(BuildingContext);
  if (!context) {
    throw new Error('useBuilding must be used within a BuildingProvider');
  }
  return context;
}
